<script setup>
import { ref } from 'vue'
import { useApi } from '../../shared/composables/useApi.js'

const props = defineProps({
  csrfToken:    { type: String, required: true },
  subMode:      { type: String, default: 'pending' },   // 'pending' | 'verify' | 'resent'
  initialError: { type: String, default: '' },
  initialSuccess:{ type: String, default: '' },
  pendingEmail: { type: String, default: '' },
})

const { post } = useApi(props.csrfToken)

const email   = ref(props.pendingEmail)
const error   = ref(props.initialError)
const success = ref(props.initialSuccess)
const sub     = ref(props.subMode)
const busy    = ref(false)

async function resend() {
  if (busy.value) return
  error.value = ''
  busy.value = true
  const { ok, data } = await post('/api/auth/verify-resend', { email: email.value })
  busy.value = false
  if (ok) {
    success.value = 'Verification email sent! Check your inbox.'
    sub.value = 'resent'
  } else {
    error.value = data.error || 'Could not send email.'
  }
}
</script>

<template>
  <div class="auth-card hf-glass">
    <template v-if="sub === 'verify' || sub === 'resent'">
      <h1>Email Verification</h1>

      <template v-if="success">
        <div class="success">{{ success }}</div>
        <div class="auth-links"><a href="/login">Log in &rarr;</a></div>
      </template>
      <template v-else-if="error">
        <div class="error">{{ error }}</div>
        <template v-if="sub !== 'resent'">
          <p style="color:var(--ink-2);font-size:0.875rem;margin-top:16px">Link expired? Enter your email to get a new one.</p>
          <form style="margin-top:12px" novalidate @submit.prevent="resend">
            <div class="form-group">
              <label for="resend-email">Email address</label>
              <input
                id="resend-email"
                v-model="email"
                type="email"
                class="hf-input"
                required
                autocomplete="email"
              />
            </div>
            <button type="submit" class="hf-btn primary auth-submit" :disabled="busy">
              {{ busy ? 'Sending…' : 'Resend verification email' }}
            </button>
          </form>
        </template>
      </template>
    </template>

    <template v-else>
      <h1>Check Your Email</h1>
      <p style="color:var(--ink-2);font-size:0.9375rem;line-height:1.6;margin-bottom:24px">
        We sent a verification link to your email address. Click the link to activate your account before logging in.
      </p>

      <div v-if="error" class="error" style="margin-bottom:16px">{{ error }}</div>
      <div v-if="success" class="success" style="margin-bottom:16px">{{ success }}</div>

      <p style="color:var(--ink-3);font-size:0.875rem;margin-bottom:16px">Didn't get it? Check your spam folder, or resend below.</p>
      <form novalidate @submit.prevent="resend">
        <div class="form-group">
          <label for="resend-email-pending">Email address</label>
          <input
            id="resend-email-pending"
            v-model="email"
            type="email"
            class="hf-input"
            required
            autocomplete="email"
          />
        </div>
        <button type="submit" class="hf-btn primary auth-submit" :disabled="busy">
          {{ busy ? 'Sending…' : 'Resend verification email' }}
        </button>
      </form>
      <div class="auth-links"><a href="/login">&larr; Back to login</a></div>
    </template>
  </div>
</template>
